import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "realFileInput", "dummyFileInput", "dropzone",
    "globalHeader", "globalStart", "globalEnd", "globalPassword", 
    "globalStartInfo", "globalEndInfo",
    "mergeCheckbox", "masterSettingsPanel", "masterFileSelect", "masterHeaderPage",
    "fileListContainer", "jsonPayload", "submitBtn", "alertBox",
    "previewFrame", "previewTitle",
    "mergeSection", "configCard", "globalSettingsSection", "fileListTitle"
  ]

  connect() {
    console.log("👉 [Diagnostic 1] Bulk Upload Controller CONNECTED successfully.");
    this.filesState = []; 
    this.masterFileId = null;
  }

  disconnect() {
    if (this.currentPreviewUrl) {
      URL.revokeObjectURL(this.currentPreviewUrl);
    }
  }

  // ✨ NEW: Action to handle toggling password visibility
  togglePassword(event) {
    const btn = event.currentTarget;
    const input = btn.previousElementSibling;
    
    const eyeIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>`;
    const eyeSlashIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>`;
    
    if (input.type === "password") {
      input.type = "text";
      btn.innerHTML = eyeSlashIcon;
    } else {
      input.type = "password";
      btn.innerHTML = eyeIcon;
    }
  }

  dragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add('border-primary', 'bg-primary-subtle');
  }

  dragLeave(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove('border-primary', 'bg-primary-subtle');
  }

  dropFiles(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove('border-primary', 'bg-primary-subtle');

    if (event.dataTransfer && event.dataTransfer.files) {
      this.processFiles(event.dataTransfer.files);
    }
  }

  filesSelected(event) {
    if (event.target.files) {
      this.processFiles(event.target.files);
    }
    
    setTimeout(() => {
      this.dummyFileInputTarget.value = "";
    }, 100);
  }

  processFiles(inputFiles) {
    try {
      if (!inputFiles || inputFiles.length === 0) return;

      const newFiles = Array.from(inputFiles);
      const globalHeaderVal = this.hasGlobalHeaderTarget ? this.globalHeaderTarget.value : 1;
      const globalStartVal = this.hasGlobalStartTarget ? this.globalStartTarget.value : 1;
      const globalEndVal = this.hasGlobalEndTarget ? this.globalEndTarget.value : "";
      const globalPassVal = this.hasGlobalPasswordTarget ? this.globalPasswordTarget.value : "";
      
      newFiles.forEach(newFile => {
        if (newFile.type === "application/pdf" || newFile.name.toLowerCase().endsWith(".pdf")) {
          const fileId = crypto.randomUUID();
          
          this.filesState.push({
            id: fileId,
            file: newFile,
            name: newFile.name,
            header_pg: globalHeaderVal,
            start_pg: globalStartVal,
            end_pg: globalEndVal,
            password: globalPassVal,
            dirty_header: false,
            dirty_start: false,
            dirty_end: false,
            dirty_password: false
          });

          if (!this.masterFileId) this.masterFileId = fileId;

        } else {
          alert(`Skipped ${newFile.name}: Only PDF files are allowed.`);
        }
      });

      this.syncRealFileInput();
      this.updateMasterSelectOptions();
      this.renderFileList();
    } catch (error) {
      console.error("❌ [Diagnostic] Critical Error processing files:", error);
    }
  }

  syncRealFileInput() {
    const dt = new DataTransfer();
    this.filesState.forEach(state => dt.items.add(state.file));
    this.realFileInputTarget.files = dt.files;
  }

  moveUp(event) {
    event.preventDefault(); event.stopPropagation();
    const index = parseInt(event.currentTarget.dataset.index);
    if (index > 0) {
      [this.filesState[index - 1], this.filesState[index]] = [this.filesState[index], this.filesState[index - 1]];
      this.syncRealFileInput();
      this.updateMasterSelectOptions();
      this.renderFileList();
    }
  }

  moveDown(event) {
    event.preventDefault(); event.stopPropagation();
    const index = parseInt(event.currentTarget.dataset.index);
    if (index < this.filesState.length - 1) {
      [this.filesState[index + 1], this.filesState[index]] = [this.filesState[index], this.filesState[index + 1]];
      this.syncRealFileInput();
      this.updateMasterSelectOptions();
      this.renderFileList();
    }
  }

  removeFile(event) {
    try {
      event.preventDefault();
      event.stopPropagation(); 

      const indexToRemove = parseInt(event.currentTarget.dataset.index);
      const removedId = this.filesState[indexToRemove].id;
      
      this.filesState.splice(indexToRemove, 1);
      this.syncRealFileInput();

      if (this.masterFileId === removedId) {
        this.masterFileId = this.filesState.length > 0 ? this.filesState[0].id : null;
      }

      this.updateMasterSelectOptions();
      this.renderFileList();
    } catch (error) {
      console.error("Error in removeFile:", error);
    }
  }

  previewFile(event) {
    try {
      event.stopPropagation(); 
      const index = parseInt(event.currentTarget.dataset.index);
      const state = this.filesState[index];

      if (state && state.file) {
        const fileURL = URL.createObjectURL(state.file);
        
        if (this.currentPreviewUrl) {
          URL.revokeObjectURL(this.currentPreviewUrl);
        }
        this.currentPreviewUrl = fileURL;

        if (this.hasPreviewFrameTarget) {
          this.previewFrameTarget.src = fileURL + "#toolbar=1&navpanes=0&view=FitH";
        }
        if (this.hasPreviewTitleTarget) {
          this.previewTitleTarget.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-danger me-2" style="width: 24px; height: 24px;"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
            Preview: <span class="d-inline-block text-truncate ms-1" style="max-width: 300px;">${state.name}</span>
          `;
        }
      }
    } catch (error) {
      console.error("Error previewing file:", error);
    }
  }

  toggleMerge() {
    const isMerging = this.hasMergeCheckboxTarget ? this.mergeCheckboxTarget.checked : false;
    if (this.hasMasterSettingsPanelTarget) {
      this.masterSettingsPanelTarget.style.display = isMerging ? 'block' : 'none';
    }
    this.renderFileList();
  }

  updateMasterSelectOptions() {
    if (!this.hasMasterFileSelectTarget) return;
    this.masterFileSelectTarget.innerHTML = "";
    
    this.filesState.forEach((state, index) => {
      const option = document.createElement("option");
      option.value = state.id;
      option.text = `${index + 1}. ${state.name}`;
      if (state.id === this.masterFileId) option.selected = true;
      this.masterFileSelectTarget.appendChild(option);
    });
  }

  masterFileChanged(event) {
    this.masterFileId = event.target.value;
    this.renderFileList();
  }

  masterHeaderChanged(event) {
    let val = parseInt(event.target.value) || 1;
    const masterState = this.filesState.find(s => s.id === this.masterFileId);
    if (masterState && masterState.start_pg < val) {
      masterState.start_pg = val;
    }
    this.renderFileList();
  }

  globalPasswordChanged(event) {
    const val = event.target.value;
    
    this.filesState.forEach((state, index) => {
      // if (!state.dirty_password) {
        state.password = val;
        // ✨ FIX: We directly target the specific input in the DOM rather than triggering 
        // a jittery full rebuild of all file components via `renderFileList()`
        const domInput = this.element.querySelector(`.indiv-password[data-index="${index}"]`);
        if (domInput) domInput.value = val;
      // }
    });

    this.updateJsonPayload(); // Pushes changes safely into hidden JSON field
  }

  globalChanged(event) {
    try {
      const fieldType = event.target.dataset.type; 
      let val = event.target.value;
      const input = event.target;

      if (fieldType === 'header') {
        let gHeader = parseInt(val) || 1;
        if (this.hasGlobalStartTarget) {
          this.globalStartTarget.min = gHeader;
          if (this.hasGlobalStartInfoTarget) {
            this.globalStartInfoTarget.setAttribute('data-info-msg', `Must be ≥ ${gHeader}`);
          }
          let gStart = parseInt(this.globalStartTarget.value) || 1;
          if (gStart < gHeader) {
            this.globalStartTarget.value = gHeader;
          }
        }
      }

      if (fieldType === 'start') {
        let gHeader = parseInt(this.hasGlobalHeaderTarget ? this.globalHeaderTarget.value : 1) || 1;
        let typedVal = parseInt(val);
        
        if (typedVal && typedVal < gHeader) {
          input.classList.add('is-invalid');
          input.title = `Value must be at least ${gHeader}`;
        } else {
          input.classList.remove('is-invalid');
          input.title = "";
        }
        
        if (this.hasGlobalEndTarget && typedVal) {
          this.globalEndTarget.min = typedVal;
        }
        if (this.hasGlobalEndInfoTarget && typedVal) {
          this.globalEndInfoTarget.setAttribute('data-info-msg', `Must be ≥ ${typedVal}`);
        }
      }

      if (fieldType === 'end') {
        let gStart = parseInt(this.hasGlobalStartTarget ? this.globalStartTarget.value : 1) || 1;
        let typedVal = parseInt(val);
        
        if (typedVal && typedVal < gStart) {
          input.classList.add('is-invalid');
          input.title = `Value must be at least ${gStart}`;
        } else {
          input.classList.remove('is-invalid');
          input.title = "";
        }
      }

      this.filesState.forEach((state, index) => {
        if (fieldType === 'header' && !state.dirty_header) {
          state.header_pg = val;
          let cascadedHeader = parseInt(val) || 1;
          let currentStart = parseInt(state.start_pg) || 1;
          
          if (!state.dirty_start && currentStart < cascadedHeader) {
            state.start_pg = val;
          }
        }
        
        if (fieldType === 'start' && !state.dirty_start) {
          state.start_pg = val;
        }
        
        if (fieldType === 'end' && !state.dirty_end) {
          state.end_pg = val;
        }
      });

      this.renderFileList();
    } catch (error) {
      console.error("Error in globalChanged:", error);
    }
  }

  individualChanged(event) {
    try {
      const input = event.target;
      const index = input.dataset.index;
      const state = this.filesState[index];

      if (input.classList.contains('indiv-password')) {
        state.dirty_password = true;
        state.password = input.value;
      }
      
      if (input.classList.contains('indiv-header')) {
        state.dirty_header = true;
        state.header_pg = parseInt(input.value) || 1;
        
        let iStart = parseInt(state.start_pg) || 1;
        if (iStart < state.header_pg) {
          state.start_pg = state.header_pg;
        }
      }
      
      if (input.classList.contains('indiv-start')) {
        state.dirty_start = true;
        state.start_pg = parseInt(input.value) || 1; 
        
        let boundingHeader = parseInt(state.header_pg) || 1;
        const isMerging = this.hasMergeCheckboxTarget ? this.mergeCheckboxTarget.checked : false;
        if (isMerging && state.id === this.masterFileId) {
          boundingHeader = parseInt(this.hasMasterHeaderPageTarget ? this.masterHeaderPageTarget.value : 1) || 1;
        } else if (isMerging) {
           boundingHeader = 1; 
        }

        if (state.start_pg < boundingHeader) {
          input.classList.add('is-invalid');
          input.title = `Value must be at least ${boundingHeader}`;
        } else {
          input.classList.remove('is-invalid');
          input.title = "";
        }
      }
      
      if (input.classList.contains('indiv-end')) {
        state.dirty_end = true;
        state.end_pg = input.value;
        
        let iStart = parseInt(state.start_pg) || 1;
        let typedVal = parseInt(input.value);
        if (typedVal && typedVal < iStart) {
          input.classList.add('is-invalid');
          input.title = `Value must be at least ${iStart}`;
        } else {
          input.classList.remove('is-invalid');
          input.title = "";
        }
      }

      this.updateJsonPayload();
    } catch (error) {
      console.error("Error in individualChanged:", error);
    }
  }

  validateBounds(event) {
    try {
      const input = event.target;
      input.classList.remove('is-invalid'); 
      input.title = "";

      if (input.dataset.type) {
        const type = input.dataset.type;
        if (type === 'start') {
          let gHeader = parseInt(this.hasGlobalHeaderTarget ? this.globalHeaderTarget.value : 1) || 1;
          let gStart = parseInt(input.value) || 1;
          if (gStart < gHeader) {
            input.value = gHeader; 
            this.globalChanged({ target: input, dataset: { type: 'start' } }); 
          }
        } else if (type === 'end' && input.value !== "") {
          let gStart = parseInt(this.hasGlobalStartTarget ? this.globalStartTarget.value : 1) || 1;
          let gEnd = parseInt(input.value);
          if (gEnd < gStart) {
            input.value = gStart; 
            this.globalChanged({ target: input, dataset: { type: 'end' } }); 
          }
        }
      } else if (input.dataset.index) {
        const index = input.dataset.index;
        const state = this.filesState[index];
        const isMerging = this.hasMergeCheckboxTarget ? this.mergeCheckboxTarget.checked : false;
        
        let boundingHeader = parseInt(state.header_pg) || 1;
        if (isMerging && state.id === this.masterFileId) {
          boundingHeader = parseInt(this.hasMasterHeaderPageTarget ? this.masterHeaderPageTarget.value : 1) || 1;
        } else if (isMerging) {
           boundingHeader = 1;
        }

        if (input.classList.contains('indiv-start')) {
          let iStart = parseInt(input.value) || 1;
          if (iStart < boundingHeader) {
            input.value = boundingHeader; 
            state.start_pg = boundingHeader;
            this.updateJsonPayload();
          }
        } else if (input.classList.contains('indiv-end') && input.value !== "") {
          let iStart = parseInt(state.start_pg) || 1;
          let iEnd = parseInt(input.value);
          if (iEnd < iStart) {
            input.value = iStart; 
            state.end_pg = iStart;
            this.updateJsonPayload();
          }
        }
      } else if (input.dataset.bulkUploadTarget === "masterHeaderPage") {
         this.masterHeaderChanged(event);
      }
    } catch (error) {
      console.error("Error in validateBounds:", error);
    }
  }

  renderFileList() {
    try {
      this.fileListContainerTarget.innerHTML = "";
      const fileCount = this.filesState.length;

      if (fileCount === 0) {
        if (this.hasAlertBoxTarget) this.alertBoxTarget.style.display = "block";
        if (this.hasSubmitBtnTarget) {
          this.submitBtnTarget.disabled = true;
          this.submitBtnTarget.classList.add("disabled");
        }
        
        if (this.hasConfigCardTarget) this.configCardTarget.style.display = "none";
        if (this.hasMergeSectionTarget) this.mergeSectionTarget.style.display = "none";

        this.updateJsonPayload();
        this.fileListContainerTarget.innerHTML = `<p class="text-muted small fst-italic">Select files to configure individual page ranges.</p>`;
        return;
      }

      if (this.hasAlertBoxTarget) this.alertBoxTarget.style.display = "none";
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false;
        this.submitBtnTarget.classList.remove("disabled");
      }

      if (fileCount === 1) {
        if (this.hasConfigCardTarget) this.configCardTarget.style.display = "block";
        
        if (this.hasMergeSectionTarget) {
          this.mergeSectionTarget.style.display = "none";
          if (this.hasMergeCheckboxTarget && this.mergeCheckboxTarget.checked) {
            this.mergeCheckboxTarget.checked = false;
            if (this.hasMasterSettingsPanelTarget) {
              this.masterSettingsPanelTarget.style.display = 'none';
            }
          }
        }
        
        if (this.hasGlobalSettingsSectionTarget) this.globalSettingsSectionTarget.style.display = "none";
        if (this.hasFileListTitleTarget) {
          this.fileListTitleTarget.innerHTML = `
            <span class="text-uppercase fw-bold text-dark d-flex align-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 20px; height: 20px;" class="text-danger me-2"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              File Configuration
            </span>`;
        }
      } else {
        if (this.hasConfigCardTarget) this.configCardTarget.style.display = "block";
        if (this.hasMergeSectionTarget) this.mergeSectionTarget.style.display = "block";
        if (this.hasGlobalSettingsSectionTarget) this.globalSettingsSectionTarget.style.display = "block";
        if (this.hasFileListTitleTarget) {
          this.fileListTitleTarget.innerHTML = `<span class="text-uppercase">Individual Files</span> - Expand to Override Global Configs - Use arrow keys for extraction sequence`;
        }
      }

      const isMerging = this.hasMergeCheckboxTarget ? this.mergeCheckboxTarget.checked : false;
      const masterHeaderPg = parseInt(this.hasMasterHeaderPageTarget ? this.masterHeaderPageTarget.value : 1) || 1;

      const ariaExpanded = (fileCount === 1) ? 'true' : 'false';
      const collapseClass = (fileCount === 1) ? 'collapse show' : 'collapse';

      this.filesState.forEach((state, index) => {
        const isMaster = isMerging && (state.id === this.masterFileId);
        const isFirst = index === 0;
        const isLast = index === this.filesState.length - 1;

        let boundingHeader = 1;
        if (isMerging) {
          if (isMaster) boundingHeader = masterHeaderPg;
        } else {
          boundingHeader = parseInt(state.header_pg) || 1;
        }

        if (state.start_pg < boundingHeader) state.start_pg = boundingHeader;
        if (state.end_pg !== "" && parseInt(state.end_pg) < state.start_pg) state.end_pg = state.start_pg;

        const masterBadge = isMaster ? `
          <span class="badge bg-primary-lt border border-primary-subtle d-inline-flex align-items-center mt-1" title="Provides the table schema for the merged document">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;" class="text-primary me-1"><path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
            Master Header Source
          </span>` : '';
        
        const headerColumnHtml = isMerging ? '' : `
          <div class="col-12 col-md-4">
            <label class="form-label small mb-1">Header Page</label>
            <input type="number" class="form-control form-control-sm indiv-header" data-index="${index}" data-action="input->bulk-upload#individualChanged blur->bulk-upload#validateBounds" value="${state.header_pg}" min="1">
          </div>
        `;

        const colClass = isMerging ? 'col-12 col-md-6' : 'col-12 col-md-4';

        const reorderButtonsHtml = fileCount > 1 ? `
          <div class="btn-group border bg-white" role="group">
            <button type="button" class="btn btn-sm btn-light px-2 ${isFirst ? 'disabled' : ''}" data-action="click->bulk-upload#moveUp" data-index="${index}" title="Move Up">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width: 14px; height: 14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /></svg>
            </button>
            <button type="button" class="btn btn-sm btn-light px-2 ${isLast ? 'disabled' : ''}" data-action="click->bulk-upload#moveDown" data-index="${index}" title="Move Down">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width: 14px; height: 14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </button>
          </div>
        ` : '';

        const fileHtml = `
          <div class="card shadow-sm border-secondary-subtle">
            <div class="card-header py-2 bg-light d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              
              <div class="d-flex align-items-center cursor-pointer flex-grow-1 w-100 accordion-toggle-btn overflow-hidden" data-bs-toggle="collapse" data-bs-target="#collapseFile${index}" aria-expanded="${ariaExpanded}" title="Click to configure page ranges">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-muted me-3 transition-icon flex-shrink-0" style="transition: transform 0.2s ease-in-out; width: 20px; height: 20px;">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                <div class="d-flex flex-column overflow-hidden flex-grow-1">
                  <h5 class="mb-0 text-dark d-flex align-items-center w-100">
                    <span class="text-muted fw-bold me-2 flex-shrink-0">${index + 1}.</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-danger me-2 flex-shrink-0" style="width: 20px; height: 20px;"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                    <div class="scrollable-filename overflow-x-auto pb-1" style="white-space: nowrap;">${state.name}</div>
                  </h5>
                  <div>${masterBadge}</div>
                </div>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 ms-lg-auto" style="z-index: 10;">
                ${reorderButtonsHtml}
                
                <div class="d-flex flex-nowrap gap-2">
                  <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center" data-action="click->bulk-upload#previewFile" data-index="${index}" data-bs-toggle="modal" data-bs-target="#pdf-preview-modal">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;" class="me-1"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Preview
                  </button>
                  <button type="button" class="btn btn-sm btn-danger d-flex align-items-center" data-action="click->bulk-upload#removeFile" data-index="${index}" title="Remove File">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;" class="me-1"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                    Remove
                  </button>
                </div>

              </div>

            </div>
            
            <div id="collapseFile${index}" class="${collapseClass}">
              <div class="card-body py-2 bg-white">
                <div class="row g-3 mb-2">
                  <div class="col-12">
                    <label class="form-label small mb-1 text-muted">File Password</label>
                    <div class="input-group input-group-sm">
                      <input type="password" class="form-control indiv-password" data-index="${index}" data-action="input->bulk-upload#individualChanged" value="${state.password || ''}" placeholder="Password (if any)">
                      <button type="button" class="btn btn-outline-secondary px-2" data-action="click->bulk-upload#togglePassword" title="Toggle visibility">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 14px; height: 14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="row g-3">
                  ${headerColumnHtml}
                  <div class="${colClass}">
                    <label class="form-label small mb-1 d-flex align-items-center">
                      Start Page
                      <span class="custom-info-tooltip ms-1 text-muted indiv-start-info" data-index="${index}" data-info-msg="Must be ≥ ${boundingHeader}">
                        <svg style="width: 12px; height: 12px;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </span>
                    </label>
                    <input type="number" class="form-control form-control-sm indiv-start" data-index="${index}" data-action="input->bulk-upload#individualChanged blur->bulk-upload#validateBounds" value="${state.start_pg}" min="${boundingHeader}">
                  </div>
                  <div class="${colClass}">
                    <label class="form-label small mb-1 d-flex align-items-center">
                      End Page
                      <span class="custom-info-tooltip ms-1 text-muted indiv-end-info" data-index="${index}" data-info-msg="Must be ≥ ${state.start_pg}">
                        <svg style="width: 12px; height: 12px;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </span>
                    </label>
                    <input type="number" class="form-control form-control-sm indiv-end" data-index="${index}" data-action="input->bulk-upload#individualChanged blur->bulk-upload#validateBounds" value="${state.end_pg}" placeholder="Last" min="${state.start_pg}">
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
        this.fileListContainerTarget.insertAdjacentHTML('beforeend', fileHtml);
      });

      this.updateJsonPayload();
    } catch (error) {
      console.error("Error in renderFileList:", error);
    }
  }

  updateJsonPayload() {
    try {
      const cleanPayload = [];
      const isMerging = this.hasMergeCheckboxTarget ? this.mergeCheckboxTarget.checked : false;
      const masterHeaderPg = parseInt(this.hasMasterHeaderPageTarget ? this.masterHeaderPageTarget.value : 1) || 1;

      this.filesState.forEach((state) => {
        const isMaster = isMerging && (state.id === this.masterFileId);
        
        let finalHeader = parseInt(state.header_pg) || 1;
        if (isMerging) {
          finalHeader = isMaster ? masterHeaderPg : -1; 
        }

        cleanPayload.push({
          filename: state.name,
          header_pg: finalHeader,
          start_pg: parseInt(state.start_pg) || 1,
          end_pg: parseInt(state.end_pg) || null,
          password: state.password
        });
      });
      
      if (this.hasJsonPayloadTarget) {
        this.jsonPayloadTarget.value = JSON.stringify(cleanPayload);
      }
    } catch (error) {
      console.error("Error in updateJsonPayload:", error);
    }
  }

  submitForm(event) {
    if (this.filesState.length === 0) {
      event.preventDefault();
      if (this.hasAlertBoxTarget) this.alertBoxTarget.style.display = "block";
    } else {
      const errorBox = document.getElementById("convertor-form-errors");
      if (errorBox) errorBox.innerHTML = "";
      
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = true;
        this.submitBtnTarget.classList.add("disabled");
        
        if (this.submitBtnTarget.tagName.toLowerCase() === 'input') {
          this.submitBtnTarget.value = "Uploading & Slicing... Please wait";
        } else {
          this.submitBtnTarget.innerText = "Uploading & Slicing... Please wait";
        }
      }
    }
  }

  resetSubmitButton(event) {
    try {
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false;
        this.submitBtnTarget.classList.remove("disabled");
        
        if (this.submitBtnTarget.tagName.toLowerCase() === 'input') {
          this.submitBtnTarget.value = "Upload & Start Conversion";
        } else {
          this.submitBtnTarget.innerText = "Upload & Start Conversion";
        }
      }

      setTimeout(() => {
        const errorBox = document.getElementById("convertor-form-errors");
        if (errorBox && errorBox.innerHTML.trim() !== "") {
          const y = errorBox.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

    } catch (error) {
      console.error("Error in resetSubmitButton:", error);
    }
  }
}